import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import { tenant, type TenantSettings } from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { FieldLabel } from '@/shared/ui/FieldLabel';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { alteracoes } from './alteracoes';

type CamposDeAcesso = Pick<
  TenantSettings,
  | 'public_login_enabled'
  | 'login_title'
  | 'login_subtitle_primary'
  | 'login_subtitle_secondary'
  | 'logo_white_url'
  | 'login_gradient_enabled'
  | 'login_gradient_colors'
  | 'login_background_url'
  | 'broker_access_enabled'
  | 'advisor_access_enabled'
  | 'broker_self_registration_enabled'
  | 'broker_signup_public_path'
  | 'password_login_enabled'
  | 'login_redirect_url'
  | 'support_url'
  | 'session_lock_idle_minutes'
  | 'password_expiry_days'
  | 'maintenance_warning_enabled'
  | 'maintenance_message'
>;

function camposDe(settings: TenantSettings): CamposDeAcesso {
  return {
    public_login_enabled: settings.public_login_enabled,
    login_title: settings.login_title,
    login_subtitle_primary: settings.login_subtitle_primary,
    login_subtitle_secondary: settings.login_subtitle_secondary,
    logo_white_url: settings.logo_white_url,
    login_gradient_enabled: settings.login_gradient_enabled,
    login_gradient_colors: settings.login_gradient_colors,
    login_background_url: settings.login_background_url,
    broker_access_enabled: settings.broker_access_enabled,
    advisor_access_enabled: settings.advisor_access_enabled,
    broker_self_registration_enabled: settings.broker_self_registration_enabled,
    broker_signup_public_path: settings.broker_signup_public_path ?? '',
    password_login_enabled: settings.password_login_enabled,
    login_redirect_url: settings.login_redirect_url,
    support_url: settings.support_url,
    session_lock_idle_minutes: settings.session_lock_idle_minutes,
    password_expiry_days: settings.password_expiry_days ?? null,
    maintenance_warning_enabled: settings.maintenance_warning_enabled,
    maintenance_message: settings.maintenance_message,
  };
}

/** Lista de cores digitada como texto: "#102030, #405060". */
function coresDoTexto(texto: string): string[] {
  return texto
    .split(',')
    .map((cor) => cor.trim())
    .filter(Boolean);
}

/**
 * Aba Acesso das configuracoes.
 *
 * E a configuracao do portal da seguradora no OnPoint: a tela de acesso (titulo, os
 * dois subtitulos, logotipo branco, degrade, imagem de fundo), as chaves de acesso e
 * o aviso de manutencao da empresa. A tela de acesso so mostra a marca antes do
 * acesso depois de publicada: sem isso, quem abre o endereco da empresa ve a tela da
 * plataforma, e ninguem descobre por ali que a empresa existe.
 *
 * O acesso de assessoria e o autocadastro de corretora existem desde o P-ACE-2: o
 * primeiro pede a funcionalidade da assessoria, e o segundo, a tela publicada; o
 * servidor confere e recusa com o motivo. O login corporativo foi descartado por
 * enquanto, e por isso o acesso por senha continua sem poder ser desligado.
 */
export function AccessSettingsPanel({
  settings,
  slug,
  podeEditar,
}: {
  settings: TenantSettings;
  slug: string;
  podeEditar: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [original, setOriginal] = useState<CamposDeAcesso>(() => camposDe(settings));
  const [form, setForm] = useState<CamposDeAcesso>(() => camposDe(settings));
  const [cores, setCores] = useState(() => settings.login_gradient_colors.join(', '));
  // Sem envio de e-mail, o link de recuperacao nao chega: a senha vencida troca na
  // propria tela de acesso, com a senha atual (ADR 0074).
  const [semEmail, setSemEmail] = useState(
    () => settings.email_delivery_available === false,
  );
  // Sem envio do link, o autocadastro registra o pedido e ele nunca chega ao assistente:
  // ninguem reenvia o link (R5-B19). A tela avisa ao ligar a chave.
  const [semLinkDoAutocadastro, setSemLinkDoAutocadastro] = useState(
    () => settings.broker_signup_email_delivery_available === false,
  );

  const salvar = useMutation({
    mutationFn: (input: Partial<TenantSettings>) => tenant.updateSettings(input),
    onSuccess: (gravado) => {
      toast.success('Acesso atualizado.');
      setOriginal(camposDe(gravado));
      setForm(camposDe(gravado));
      setCores(gravado.login_gradient_colors.join(', '));
      setSemEmail(gravado.email_delivery_available === false);
      setSemLinkDoAutocadastro(
        gravado.broker_signup_email_delivery_available === false,
      );
      void queryClient.invalidateQueries({ queryKey: ['tenant'] });
    },
  });
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  function set<K extends keyof CamposDeAcesso>(campo: K, valor: CamposDeAcesso[K]) {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const proximo = { ...form, login_gradient_colors: coresDoTexto(cores) };
    const mudancas = alteracoes(original, proximo);
    if (Object.keys(mudancas).length === 0) {
      toast.info('Nada mudou.');
      return;
    }
    salvar.mutate(mudancas);
  }

  const enderecoPublico =
    typeof window === 'undefined'
      ? `/entrar/${slug}`
      : `${window.location.origin}/entrar/${slug}`;
  const raizDoSite = typeof window === 'undefined' ? '' : window.location.origin;

  return (
    <form onSubmit={enviar} className="flex flex-col gap-5">
      {salvar.isError && !erro?.details?.['field'] ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {salvar.error.message}
        </Alert>
      ) : null}

      <fieldset
        disabled={!podeEditar || salvar.isPending}
        className="flex flex-col gap-5"
      >
        <Card>
          <CardHeader
            title="Tela de acesso"
            description="O que aparece antes do acesso, no endereco da empresa."
          />
          <CardBody className="flex flex-col gap-4">
            <Chave
              label="Publicar a tela de acesso da empresa"
              checked={form.public_login_enabled}
              onChange={(valor) => set('public_login_enabled', valor)}
              detalhe={`Ligada, quem abre ${enderecoPublico} ve a marca da empresa. Desligada, a tela e a da plataforma, igual a de uma empresa que nao existe.`}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Titulo da tela de acesso"
                value={form.login_title}
                maxLength={120}
                placeholder="Bem-vindo ao Portal"
                error={fieldErrorOf(erro, 'login_title')}
                onChange={(evento) => set('login_title', evento.target.value)}
              />
              <Field
                label="Primeiro subtitulo"
                value={form.login_subtitle_primary}
                maxLength={120}
                error={fieldErrorOf(erro, 'login_subtitle_primary')}
                onChange={(evento) =>
                  set('login_subtitle_primary', evento.target.value)
                }
              />
            </div>
            <Field
              label="Segundo subtitulo"
              value={form.login_subtitle_secondary}
              maxLength={200}
              error={fieldErrorOf(erro, 'login_subtitle_secondary')}
              onChange={(evento) =>
                set('login_subtitle_secondary', evento.target.value)
              }
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="URL do logotipo branco"
                value={form.logo_white_url}
                placeholder="https://"
                error={fieldErrorOf(erro, 'logo_white_url')}
                onChange={(evento) => set('logo_white_url', evento.target.value)}
              />
              <Field
                label="URL da imagem de fundo"
                value={form.login_background_url}
                placeholder="https://"
                error={fieldErrorOf(erro, 'login_background_url')}
                onChange={(evento) => set('login_background_url', evento.target.value)}
              />
            </div>
            <Chave
              label="Fundo em degrade"
              checked={form.login_gradient_enabled}
              onChange={(valor) => set('login_gradient_enabled', valor)}
            />
            <Field
              label="Cores do degrade"
              value={cores}
              placeholder="#0a1b3d, #1d6cf2"
              error={fieldErrorOf(erro, 'login_gradient_colors')}
              onChange={(evento) => setCores(evento.target.value)}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Acesso" description="Quem entra e por onde." />
          <CardBody className="flex flex-col gap-4">
            <Chave
              label="Mostrar a tela de acesso do corretor"
              checked={form.broker_access_enabled}
              onChange={(valor) => set('broker_access_enabled', valor)}
              detalhe={`A tela ${enderecoPublico}/corretor, com a marca da empresa.`}
            />
            <Chave
              label="Acesso de assessoria"
              checked={form.advisor_access_enabled}
              onChange={(valor) => set('advisor_access_enabled', valor)}
              detalhe={`A tela ${enderecoPublico}/assessor. Pede a funcionalidade da assessoria, ligada pela administracao da plataforma; as pessoas da assessoria sao ligadas na tela de cada assessoria.`}
            />
            <Chave
              label="Autocadastro de corretora"
              checked={form.broker_self_registration_enabled}
              onChange={(valor) => set('broker_self_registration_enabled', valor)}
              detalhe={`A tela ${enderecoPublico}/cadastro-corretora, com a marca da empresa. Pede a tela de acesso publicada; o pedido chega na esteira Cadastro para aprovacao.`}
            />
            {semLinkDoAutocadastro && form.broker_self_registration_enabled ? (
              <Alert tone="warning" title="Link do autocadastro sem envio">
                O envio de e-mail nao esta disponivel nesta instalacao: o link do
                autocadastro nao chega a corretora, e ninguem pode reenvia-lo. O pedido
                fica registrado e e apagado quando o link vence, sem chegar ao
                assistente.
              </Alert>
            ) : null}
            <Field
              label="Endereco do cadastro publico de corretores"
              value={form.broker_signup_public_path ?? ''}
              placeholder="Ex.: sudaseg"
              error={fieldErrorOf(erro, 'broker_signup_public_path')}
              hint={
                form.broker_signup_public_path
                  ? `O cadastro publico fica em ${raizDoSite}/${form.broker_signup_public_path.trim().toLowerCase()}. So abre com o autocadastro ligado, a consulta a SUSEP configurada no servidor e o envio de e-mail ligado na administracao da plataforma; o acesso da corretora vale depois do clique no link de ativacao enviado ao e-mail.`
                  : 'Opcional. Letras minusculas, digitos e hifen, como sudaseg. Vazio, nao ha cadastro publico de corretores.'
              }
              onChange={(evento) =>
                set('broker_signup_public_path', evento.target.value)
              }
            />
            {settings.broker_signup_public_path_warning &&
            form.broker_signup_public_path ? (
              <Alert tone="warning" title="Cadastro publico sem envio de e-mail">
                {settings.broker_signup_public_path_warning}
              </Alert>
            ) : null}
            <Chave
              label="Acesso por e-mail e senha"
              checked={form.password_login_enabled}
              onChange={(valor) => set('password_login_enabled', valor)}
              bloqueada="So pode ser desligado quando houver login corporativo, que foi deixado para depois."
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Contato de suporte"
                value={form.support_url}
                placeholder="https:// ou mailto:"
                error={fieldErrorOf(erro, 'support_url')}
                onChange={(evento) => set('support_url', evento.target.value)}
              />
              <Field
                label="Endereco de redirecionamento do acesso"
                value={form.login_redirect_url}
                placeholder="https://"
                error={fieldErrorOf(erro, 'login_redirect_url')}
                onChange={(evento) => set('login_redirect_url', evento.target.value)}
              />
            </div>
            <Field
              label="Bloqueio por inatividade (minutos)"
              type="number"
              min={0}
              max={480}
              value={String(form.session_lock_idle_minutes)}
              hint="Zero desliga. Ligado, de 5 a 480 minutos."
              error={fieldErrorOf(erro, 'session_lock_idle_minutes')}
              onChange={(evento) =>
                set(
                  'session_lock_idle_minutes',
                  Number.parseInt(evento.target.value || '0', 10) || 0,
                )
              }
            />
            {/* R-1 da auditoria de paridade: o prazo de validade da senha. O valor do
                OnPoint nao tem fonte, e o campo nasce vazio, que e desligado. */}
            <Field
              label="Prazo de validade da senha (dias)"
              type="number"
              min={0}
              max={3650}
              value={form.password_expiry_days ? String(form.password_expiry_days) : ''}
              hint="Vazio ou zero desliga. Ligado, quem trocou a senha ha mais dias que o prazo cria uma senha nova antes de entrar, na propria tela de acesso, com a senha atual."
              error={fieldErrorOf(erro, 'password_expiry_days')}
              onChange={(evento) =>
                set(
                  'password_expiry_days',
                  Number.parseInt(evento.target.value || '0', 10) || 0,
                )
              }
            />
            {semEmail && form.password_expiry_days ? (
              <Alert tone="warning" title="Envio de e-mail indisponivel">
                O envio de e-mail nao esta disponivel nesta instalacao: o link de
                recuperacao de senha nao chega. Com o prazo ligado, quem tiver a senha
                vencida cria a senha nova na propria tela de acesso, com a senha atual.
              </Alert>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Aviso de manutencao da empresa"
            description="Faixa no topo de todas as telas e na tela de acesso. A manutencao da plataforma e outra, decidida pela administracao da plataforma."
          />
          <CardBody className="flex flex-col gap-4">
            <Chave
              label="Ligar o aviso de manutencao"
              checked={form.maintenance_warning_enabled}
              onChange={(valor) => set('maintenance_warning_enabled', valor)}
            />
            <Textarea
              label="Mensagem do aviso de manutencao"
              value={form.maintenance_message}
              rows={3}
              maxLength={500}
              error={fieldErrorOf(erro, 'maintenance_message')}
              onChange={(evento) => set('maintenance_message', evento.target.value)}
            />
          </CardBody>
        </Card>
      </fieldset>

      {podeEditar ? (
        <div className="flex justify-end">
          <Button type="submit" loading={salvar.isPending}>
            Salvar acesso
          </Button>
        </div>
      ) : (
        <Alert tone="info" title="Somente leitura">
          O seu perfil nao inclui a alteracao das configuracoes da empresa.
        </Alert>
      )}
    </form>
  );
}

/**
 * Chave liga e desliga com rotulo, ajuda do dicionario e, quando a plataforma ainda
 * nao tem o que a chave liga, o motivo de ela nao mudar.
 */
function Chave({
  label,
  checked,
  onChange,
  detalhe,
  bloqueada,
}: {
  label: string;
  checked: boolean;
  onChange: (valor: boolean) => void;
  detalhe?: string;
  bloqueada?: string;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-2.5">
      <input
        id={id}
        type="checkbox"
        role="switch"
        aria-checked={checked}
        className="mt-0.5 size-4 accent-[var(--color-brand-500)]"
        checked={checked}
        disabled={bloqueada !== undefined}
        onChange={(evento) => onChange(evento.target.checked)}
      />
      <div className="flex flex-col gap-0.5">
        <FieldLabel htmlFor={id} label={label} className="text-sm text-ink-800" />
        {bloqueada ? <span className="text-xs text-ink-500">{bloqueada}</span> : null}
        {!bloqueada && detalhe ? (
          <span className="text-xs text-ink-500">{detalhe}</span>
        ) : null}
      </div>
    </div>
  );
}
